import { useEffect } from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import { brandingFixture } from '../lib/branding/branding-fixture';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchPendingList } from '../store/thunks/pending-thunks';

/**
 * Admin landing page — the dashboard visible only to members of
 * the `admin` Cognito group. Shows a queue-depth summary plus
 * quick links to the pending queue and the submit form.
 */
export default function AdminLandingPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const total = useAppSelector((state) => state.pending.total);
  const listStatus = useAppSelector((state) => state.pending.listStatus);

  useEffect(() => {
    void dispatch(fetchPendingList(1));
  }, [dispatch]);

  return (
    <Stack spacing={3} sx={{ maxWidth: 720, mx: 'auto', mt: 4 }}>
      <SeoHead
        route="/admin"
        pageTitle="Admin"
        branding={brandingFixture.share}
        jsonLdType="WebSite"
      />
      <Typography variant="h1" component="h1" sx={{ fontSize: '1.75rem' }}>
        Admin
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Review the pending content queue and approve or reject items. Submissions from the
        content-ingestion pipeline (or manual submissions below) land here for review.
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Pending queue
              </Typography>
              <Typography variant="h2" component="p" sx={{ fontSize: '2.5rem', fontWeight: 600 }}>
                {listStatus === 'succeeded' ? total : '…'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                items awaiting review
              </Typography>
            </CardContent>
            <CardActions>
              <Button component={RouterLink} to="/admin/pending" variant="contained">
                Review queue
              </Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Manual submission
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                Create a new pending fact, news, or release by hand. Useful for bootstrapping
                content while the auto-ingestion pipeline is stood up.
              </Typography>
            </CardContent>
            <CardActions>
              <Button component={RouterLink} to="/admin/submit" variant="outlined">
                Submit content
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
